import { useTranslation } from "react-i18next";
import { Checkbox } from "../../../components/fields/base/Checkbox";
import { type SelectedRowData } from "../SearchTermReportDashboardTable";
import { extractAsin } from "../../../utils/extractAsin";
import { Flex } from "../../../../styled-system/jsx";
import { css } from "../../../../styled-system/css";
import { getValidKeywordToNegate } from "../../advertising/utils/getValidKeywordsToNegate";

const QueryNegationMatchTypeSelector = ({
  selectedMatchTypes,
  setSelectedMatchTypes,
  selectedRowsData,
}: {
  selectedMatchTypes: string[];
  setSelectedMatchTypes: React.Dispatch<React.SetStateAction<string[]>>;
  selectedRowsData: Record<string, SelectedRowData>;
}) => {
  const { t } = useTranslation("str");
  const invalidKeywords = Object.values(selectedRowsData)
    .map((row) => row.query)
    .filter((keyword) => {
      const isAsin = !!extractAsin(keyword);
      if (isAsin) return false;

      return selectedMatchTypes.some((mt) => !getValidKeywordToNegate(mt, keyword));
    });
  const validKeywords = Object.values(selectedRowsData).filter(
    (row) => !extractAsin(row.query) && !invalidKeywords.includes(row.query),
  );
  const targetCount = Object.values(selectedRowsData).filter((row) => !!extractAsin(row.query)).length;
  const keywordCount = validKeywords.length;

  const matchTypeLabel = (matchType: string): string => {
    if (matchType === "negative_exact") {
      return t(($) => $.str.match_type["Negative exact"]);
    }
    if (matchType === "negative_phrase") {
      return t(($) => $.str.match_type["Negative phrase"]);
    }
    return matchType;
  };

  return (
    <>
      {keywordCount > 0 && (
        <Flex direction={"column"} alignItems={"center"}>
          <p className={css({ paddingX: "xlarge", paddingBottom: "medium", textAlign: "center" })}>
            {t(($) => $.str.negate["Select the match types you want to negate for the selected queries."], {
              keywordCount,
            })}
            <span>
              {targetCount ?
                t(
                  ($) =>
                    $.str.negate[
                      "ASIN targets don't require match types, and the selected ASIN queries will be added as negative product targets."
                    ],
                  { targetCount },
                )
              : ""}
            </span>
          </p>
          <div className={css({ paddingX: "large" })}>
            {["negative_exact", "negative_phrase"].map((matchType) => (
              <Checkbox
                isDisabled={!getValidKeywordToNegate(matchType, "")}
                key={matchType}
                label={matchTypeLabel(matchType)}
                value={selectedMatchTypes.includes(matchType)}
                onChange={(checked) =>
                  setSelectedMatchTypes((prev) =>
                    checked ? [...prev, matchType] : prev.filter((t) => t !== matchType),
                  )
                }
              />
            ))}
          </div>
        </Flex>
      )}
      {invalidKeywords.length > 0 && (
        <Flex
          className={css({
            borderRadius: "lg",
            width: "100%",
            marginTop: "medium",
            padding: "medium",
            background: "lightgrey",
          })}
          direction="column"
        >
          <p className={css({ color: "red.600", fontWeight: 600 })}>
            {t(($) => $.str.negate["These queries cannot be negated:"])}
          </p>
          <ul className={css({ paddingLeft: "large", color: "red.700" })}>
            {invalidKeywords.map((kw) => (
              <li key={kw}>{kw}</li>
            ))}
          </ul>
          <p className={css({ marginTop: "small", color: "gray.700", fontSize: "small" })}>
            {t(($) => $.str.negate["These will be ignored when negating unless you adjust the match type."])}
          </p>
        </Flex>
      )}
    </>
  );
};

export default QueryNegationMatchTypeSelector;
